// src/utils/axiosConfig.js
import axios from "axios";
import { jwtDecode } from "jwt-decode";

/**
 * ✅ API base url (keep trailing slash)
 */
export const base_url = "https://api.kupto.co/api/";

/* =========================
   TOKEN HELPERS
========================= */

export const isTokenValid = (token) => {
  if (!token || token === "undefined" || token === "null") return false;

  try {
    const decoded = jwtDecode(token);
    const now = Date.now() / 1000;
    return !!decoded?.exp && decoded.exp > now + 5;
  } catch {
    return false;
  }
};

/**
 * Returns a VALID access token if present.
 * If expired/invalid, returns null.
 */
export const getToken = () => {
  try {
    const customerRaw = localStorage.getItem("customer");
    if (customerRaw) {
      const customer = JSON.parse(customerRaw);
      const t = customer?.token || customer?.accessToken;
      if (isTokenValid(t)) return t;
    }
  } catch {}

  try {
    const t = localStorage.getItem("token");
    return isTokenValid(t) ? t : null;
  } catch {
    return null;
  }
};

export const setToken = (newToken) => {
  if (!newToken) return;

  localStorage.setItem("token", newToken);

  try {
    const customerRaw = localStorage.getItem("customer");
    const customer = customerRaw ? JSON.parse(customerRaw) : {};
    customer.token = newToken;
    customer.accessToken = newToken;
    localStorage.setItem("customer", JSON.stringify(customer));
  } catch {}
};

export const clearAuth = () => {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("customer");
  } catch {}
};

export const saveReturnTo = () => {
  try {
    const path = `${window.location.pathname}${window.location.search || ""}`;
    sessionStorage.setItem("kupto_returnTo", path);
  } catch {}
};

/* =========================
   HEADER CONFIG HELPERS
========================= */

/**
 * requiresAuth=true marks the request as protected even if
 * there is currently no valid access token in localStorage.
 * That allows the response interceptor to try /user/refresh
 * using the refresh cookie.
 */
export const getConfig = (extraHeaders = {}, requiresAuth = false) => {
  const token = getToken();

  return {
    withCredentials: true,
    requiresAuth,
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...extraHeaders,
    },
  };
};



export const getAuthConfig = (extraHeaders = {}) => {
  const token = getToken();

  return {
    withCredentials: true,
    requiresAuth: true,
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...extraHeaders,
    },
  };
};

/* =========================================================
   ✅ AXIOS INSTANCE WITH AUTO-REFRESH
   - refreshes on 401 for protected requests
   - protected requests can still refresh even if access token
     has already expired and no Authorization header was sent
   - avoids hard redirect for random public 401s
========================================================= */

const api = axios.create({
  baseURL: base_url,
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    const token = getToken();

    config.headers = config.headers || {};
    config.withCredentials = true;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config._hasAuth = true;
    } else {
      delete config.headers.Authorization;

      // ✅ If caller marked request as protected, still allow refresh flow
      config._hasAuth = !!config.requiresAuth;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

let refreshPromise = null;

const refreshAccessToken = async () => {
  const res = await axios.post(
    `${base_url}user/refresh`,
    {},
    {
      withCredentials: true,
      headers: { Accept: "application/json" },
    }
  );

  const newToken = res.data?.token || res.data?.accessToken || "";

  if (newToken) {
    setToken(newToken);
    api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
    return newToken;
  }

  return null;
};

const redirectToLogin = () => {
  saveReturnTo();
  clearAuth();

  if (!window.location.pathname.startsWith("/identification")) {
    window.location.href = "/identification";
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;

    if (!error?.response || !originalRequest) {
      return Promise.reject(error);
    }

    const status = Number(error.response.status || 0);
    const url = String(originalRequest.url || "");

    const isRefreshCall =
      url.includes("user/refresh") ||
      url.endsWith("/user/refresh") ||
      url.includes("/api/user/refresh");

    const isAuthPage =
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/identification");

    // ✅ If refresh itself fails, session is gone
    if (isRefreshCall && status === 401) {
      if (!isAuthPage) redirectToLogin();
      return Promise.reject(error);
    }

    // ✅ Only handle 401
    if (status !== 401) {
      return Promise.reject(error);
    }

    // ✅ Only refresh protected/auth requests
    if (!originalRequest._hasAuth) {
      return Promise.reject(error);
    }

    // ✅ Prevent infinite retry loop
    if (originalRequest._retry) {
      if (!isAuthPage) redirectToLogin();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }

      const newToken = await refreshPromise;

      if (!newToken) {
        if (!isAuthPage) redirectToLogin();
        return Promise.reject(error);
      }

      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      originalRequest.withCredentials = true;
      originalRequest._hasAuth = true;

      return api(originalRequest);
    } catch (refreshErr) {
      if (!isAuthPage) redirectToLogin();
      return Promise.reject(refreshErr);
    }
  }
);

export default api;